/**
 * Frontend Pay Period Utilities
 * Fully config-driven — pay period type and dates are set by the superadmin
 * in Settings. The system generates all periods from the anchor date,
 * and individual periods can be overridden by the superadmin.
 */

const _addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };
const _toISO = (d) => d.toISOString().split('T')[0];

function getFirstPeriod(type, anchor, offset) {
  const day = anchor.getDate();
  if (type === 'semimonthly') {
    if (day <= 15) {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
      const e = new Date(anchor.getFullYear(), anchor.getMonth(), 15);
      return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
    } else {
      const s = new Date(anchor.getFullYear(), anchor.getMonth(), 16);
      const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
      return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
    }
  } else if (type === 'biweekly') {
    const e = _addDays(anchor, 13);
    return { start: _toISO(anchor), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  } else if (type === 'weekly') {
    const e = _addDays(anchor, 6);
    return { start: _toISO(anchor), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  } else if (type === 'monthly') {
    const s = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const e = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0);
    return { start: _toISO(s), end: _toISO(e), payDate: _toISO(_addDays(e, offset)) };
  }
  return null;
}

function getNextPeriod(type, prevEnd, offset) {
  const next = _addDays(new Date(prevEnd + 'T00:00:00'), 1);
  return getFirstPeriod(type, next, offset);
}

/**
 * Given org payPeriodConfig, returns the current pay period
 * (the one that contains today), with cascading overrides applied.
 *
 * Returns: { start: 'YYYY-MM-DD', end: 'YYYY-MM-DD', payDate: 'YYYY-MM-DD' }
 * or null if config is missing.
 */
export function getCurrentPayPeriod(payPeriodConfig) {
  return getPayPeriodForDate(payPeriodConfig, new Date());
}

/**
 * Same as getCurrentPayPeriod, but returns the pay period containing
 * an arbitrary target date instead of always using today.
 */
export function getPayPeriodForDate(payPeriodConfig, targetDate) {
  if (!payPeriodConfig?.anchorDate) return null;

  const type = payPeriodConfig.type || 'semimonthly';
  const offset = parseInt(payPeriodConfig.payDateOffsetDays) || 5;
  const overrides = payPeriodConfig.overrides || {};
  const target = _toISO(targetDate instanceof Date ? targetDate : new Date(targetDate));

  // Build periods with cascading overrides up to 200 periods
  const anchor = new Date(payPeriodConfig.anchorDate + 'T00:00:00');
  let raw = getFirstPeriod(type, anchor, offset);
  let lastMatchedFuture = null;

  for (let i = 0; i < 200; i++) {
    if (!raw) break;

    // Apply override for this index if it exists, cascading from previous end
    const resolved = overrides[i]
      ? { ...overrides[i] }
      : raw;

    // Is the target date within this period?
    if (target >= resolved.start && target <= resolved.end) {
      return resolved;
    }

    // Track the nearest future period in case target is between periods
    if (resolved.start > target && !lastMatchedFuture) {
      lastMatchedFuture = resolved;
    }

    // Past this period — get next, cascading from resolved end
    const next = getNextPeriod(type, resolved.end, offset);
    if (!next) break;
    raw = next;
  }

  return lastMatchedFuture || raw;
}

/**
 * Format a YYYY-MM-DD string for display.
 * e.g. '2026-03-16' → 'Mar 16, 2026'
 */
export function formatPayDate(isoStr) {
  if (!isoStr) return '';
  const d = new Date(isoStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Fetch the org's payPeriodConfig from the API.
 * Returns the config object or null.
 */
export async function fetchPayPeriodConfig() {
  try {
    const stored = localStorage.getItem('user');
    if (!stored) return null;
    const user = JSON.parse(stored);
    const res = await fetch(`/api/organizations/${user.organizationId}`, {
      headers: { Authorization: `Bearer ${user.token}` }
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.payPeriodConfig || null;
  } catch {
    return null;
  }
}